import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  ADHERENCE_BAND_HIGH,
  ADHERENCE_BAND_LOW,
  type CoachAthleteCard,
  type CoachAthleteFlag,
  type TargetZone,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { Avatar, type AvatarTone } from "../primitives/Avatar";
import { Badge, type BadgeTone } from "../primitives/Badge";
import { type IconName } from "../Icon/Icon";
import { Card } from "../primitives/Card";
import { StatPair } from "../primitives/StatPair";
import { TargetBar } from "../primitives/TargetBar";

// <AthleteCardRow> — one athlete's summary card: avatar + name + pinned block,
// attention badges, adherence stat + bar. Extracted from CoachDashboard when
// the CO-9 detail header became the 2nd callsite: the dashboard row and the
// detail's identity header MUST read the same flags/zone/adherence the same
// way, or the two surfaces drift. Pressable only when onPress is wired (the
// CO-9 drill-down); static otherwise.

// Flag → badge tone + optional icon. The label word carries the meaning; the
// tint + icon only reinforce it (never colour-alone). Icons, not text glyphs:
// a glyph is drawn by whatever font the device has and can't take a precise
// size, which is how the timeline's «⬇️» ended up wrong (IB-10).
const FLAG_BADGE: Record<
  CoachAthleteFlag,
  { tone: BadgeTone; icon?: IconName; key: string }
> = {
  stale: { tone: "danger", icon: "warn", key: "coach.dashboard.flagStale" },
  "under-target": { tone: "warn", key: "coach.dashboard.flagUnderTarget" },
  "on-track": { tone: "ok", icon: "check", key: "coach.dashboard.flagOnTrack" },
  "no-program": { tone: "neutral", key: "coach.dashboard.flagNoProgram" },
};

// Avatar tint echoes the athlete's attention FLAGS (not raw priority): stale →
// danger, under-target → warn, else neutral — so the filled-circle tint (a
// high-contrast cue: white initials on a solid fill) never contradicts the flag
// Badge beside it. A "no program only" athlete stays neutral, matching its
// neutral badge. The card itself is never tinted (see Card.tsx — that lands
// sub-4.5:1 text on the fill); priority reads via this avatar + the badges.
const avatarTone = (flags: readonly CoachAthleteFlag[]): AvatarTone =>
  flags.includes("stale")
    ? "danger"
    : flags.includes("under-target")
      ? "warn"
      : "neutral";

const zoneColor = (theme: UiTheme, zone: TargetZone | null): string =>
  zone === "in-band"
    ? theme.palette.complete
    : zone === "over"
      ? theme.palette.warn
      : zone === "under"
        ? theme.palette.danger
        : theme.palette.ink;

export function AthleteCardRow({
  theme,
  card,
  onPress,
}: {
  theme: UiTheme;
  card: CoachAthleteCard;
  onPress?: (sub: string) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const last =
    card.daysSinceLast == null
      ? t("coach.dashboard.lastNever")
      : card.daysSinceLast === 0
        ? t("coach.dashboard.lastToday")
        : t("coach.dashboard.lastDaysAgo", { count: card.daysSinceLast });
  const adherenceStr =
    card.adherencePct != null ? `${card.adherencePct}%` : "—";
  const a11yLabel = [
    card.name,
    card.blockName ?? t("coach.dashboard.noProgram"),
    `${t("coach.dashboard.adherence")} ${adherenceStr}`,
    last,
  ].join(", ");

  const body = (
    <Card theme={theme}>
      <View style={styles.rowTop}>
        <Avatar theme={theme} name={card.name} tone={avatarTone(card.flags)} />
        <View style={styles.main}>
          <View style={styles.nameLine}>
            <Text style={styles.name} numberOfLines={1}>
              {card.name}
            </Text>
            <Text style={styles.block} numberOfLines={1}>
              {card.blockName ?? t("coach.dashboard.noProgram")}
            </Text>
          </View>
          {card.flags.length > 0 ? (
            <View style={styles.tags}>
              {card.flags.map((f) => (
                <Badge
                  key={f}
                  theme={theme}
                  tone={FLAG_BADGE[f].tone}
                  icon={FLAG_BADGE[f].icon}
                  label={t(FLAG_BADGE[f].key)}
                />
              ))}
            </View>
          ) : null}
        </View>
        <View style={styles.metrics}>
          <StatPair
            theme={theme}
            value={adherenceStr}
            label={t("coach.dashboard.adherence")}
            valueColor={zoneColor(theme, card.adherenceZone)}
          />
          <Text style={styles.last} numberOfLines={1}>
            {last}
          </Text>
        </View>
      </View>
      {card.adherencePct != null ? (
        <View style={styles.bar}>
          <TargetBar
            theme={theme}
            actual={card.adherencePct}
            target={100}
            bandLow={ADHERENCE_BAND_LOW}
            bandHigh={ADHERENCE_BAND_HIGH}
            zone={card.adherenceZone}
          />
        </View>
      ) : null}
    </Card>
  );

  // Only claim the interactive "button" role when a handler is actually wired
  // (the CO-9 drill-down). Until then the row is a static summary — announcing
  // a button that does nothing would mislead a screen-reader user.
  if (!onPress) {
    return (
      <View accessible accessibilityLabel={a11yLabel}>
        {body}
      </View>
    );
  }
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      onPress={() => onPress(card.sub)}
      style={({ pressed }) => (pressed ? styles.pressed : null)}
    >
      {body}
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    pressed: { opacity: 0.7 },
    rowTop: { flexDirection: "row", alignItems: "center", gap: s.s4 },
    main: { flex: 1, minWidth: 0, gap: s.s2 },
    nameLine: {
      flexDirection: "row",
      alignItems: "baseline",
      gap: s.s3,
      flexWrap: "wrap",
    },
    name: { color: p.ink, fontSize: ty.sizes.md, fontWeight: "700" },
    block: { color: p.ink3, fontSize: ty.sizes.sm },
    tags: { flexDirection: "row", flexWrap: "wrap", gap: s.s2 },
    metrics: { alignItems: "flex-end", gap: 2, flexShrink: 0 },
    last: { color: p.ink3, fontSize: ty.sizes.xs },
    bar: { marginTop: s.s3 },
  });
}
